import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { CharacterAvatar } from "@/components/characters/CharacterAvatar";
import { Mail, Loader2 } from "lucide-react";

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ForgotPasswordDialog = ({ open, onOpenChange }: ForgotPasswordDialogProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPasswordRequest } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await resetPasswordRequest(email);
      
      if (error) throw error;

      setSuccess(true);
      toast({
        title: "Check your email! 📧",
        description: "If that email exists, we've sent you a password reset link.",
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        onOpenChange(false);
        setSuccess(false);
        setEmail("");
      }, 5000);
    } catch (error: any) {
      // Show generic message for security (don't reveal if email exists)
      toast({
        title: "Check your email! 📧",
        description: "If that email exists, we've sent you a password reset link.",
      });
      
      // Still close the dialog
      setTimeout(() => {
        onOpenChange(false);
        setSuccess(false);
        setEmail("");
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <CharacterAvatar character="buddy" size="lg" animated />
          </div>
          <DialogTitle className="text-center font-bubblegum text-3xl text-gray-800">
            Forgot Your Password? 🔐
          </DialogTitle>
          <DialogDescription className="text-center font-comic text-base text-gray-600">
            Don't worry, we'll help you get back in!
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-6 space-y-4">
            <div className="w-20 h-20 mx-auto bg-kid-green/20 rounded-full flex items-center justify-center">
              <Mail className="w-10 h-10 text-kid-green" />
            </div>
            <div>
              <h3 className="font-fredoka font-bold text-xl text-gray-800 mb-2">
                Email Sent! ✨
              </h3>
              <p className="font-comic text-gray-600">
                Check your inbox (and spam folder) for a password reset link.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email" className="font-fredoka font-semibold text-gray-700">
                Email Address
              </Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="you@example.com"
                className="h-12 text-base rounded-xl border-2 focus:border-kid-purple"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-lg rounded-xl bg-gradient-to-r from-kid-purple to-kid-pink font-fredoka font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 mr-2" />
                  Send Reset Link 📧
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full font-comic text-gray-600 hover:text-gray-800"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              ← Back to login
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
